import { useEffect, useState, RefObject } from 'react';
import './useStickyMenu.scss';

/**
 * Hook to create a sticky menu that fixes to the top of the viewport when scrolled
 *
 * @param headerRef - Reference to the header element
 * @returns Boolean indicating if the header is in sticky mode
 *
 * @example
 * ```tsx
 * const headerRef = useRef<HTMLDivElement>(null);
 * const isSticky = useStickyMenu(headerRef);
 *
 * return (
 *   <header ref={headerRef} className={isSticky ? 'sticky' : ''}>
 *     Menu content
 *   </header>
 * );
 * ```
 */
export const useStickyMenu = (
  headerRef: RefObject<HTMLElement>
): boolean => {
  const [isSticky, setIsSticky] = useState(false);
  const [initialPosition, setInitialPosition] = useState<number | null>(null);

  useEffect(() => {
    if (!headerRef.current) return;

    // Store the initial position of the header relative to the document
    const headerRect = headerRef.current.getBoundingClientRect();
    const initialPos = headerRect.top + window.scrollY;
    setInitialPosition(initialPos);

    const handleScroll = () => {
      if (!headerRef.current || initialPosition === null) return;

      // Get the header's position relative to the viewport
      const headerTop = headerRef.current.getBoundingClientRect().top;
      
      // Calculate the scroll position where the header should become sticky
      const stickyPoint = 0;
      
      // Calculate the scroll position where the header should return to normal
      // This is the initial position of the header minus a small buffer
      const returnPoint = initialPosition - 10; // 10px buffer
      
      if (headerTop <= stickyPoint && window.scrollY > returnPoint) {
        // Make sticky when header reaches top of viewport and we're scrolled past the return point
        if (!isSticky) {
          setIsSticky(true);
        }
      } else {
        // Return to normal position
        if (isSticky) {
          setIsSticky(false);
        }
      }
    };

    // Initial check
    handleScroll();

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headerRef, isSticky, initialPosition]);

  // If the menu is sticky, add padding to the body to prevent content jump
  useEffect(() => {
    if (!headerRef.current) return;
    
    if (isSticky) {
      document.body.style.paddingTop = `${headerRef.current.offsetHeight}px`;
    } else {
      document.body.style.paddingTop = '0';
    }

    return () => {
      document.body.style.paddingTop = '0';
    };
  }, [isSticky, headerRef]);
  
  return isSticky;
};
